import { ISAssignment } from '@/features/assignments/types/assignment';
import { ISWorkspaceFeedbackTemplate } from '@/features/focus-mode/types/feedback-template';

import { IQuickReply } from '@/shared/services/forms/forms.types';
export type TWorkspaceMemberRole = 'ADMIN' | 'FORM_CREATOR' | 'REFERRER';

export const APPLICANT_MODE_WORKSPACE_ID = 'APPLICANT_MODE';

/** Workspace-level Google Calendar Domain-Wide Delegation config — returned inside
 *  `workspace` on `settings/fetch-details`. `status === 'VERIFIED'` means org-wide
 *  calendar access is live for the listed `domains`. */
export type TGCalDWDStatus = 'VERIFIED' | 'FAILED' | 'PENDING' | null;

export interface IGCalDWDInfo {
	serviceAccountEmail: string;
	oauthClientId: string;
	scopes: string[];
	/** True when the workspace is on Business plan; gates the DWD feature. */
	businessEnabled: boolean;
	status: TGCalDWDStatus;
	domains: string[] | null;
	verifiedAt: string | null;
}

export interface WorkspaceMember {
	userId?: number;
	name: string;
	email: string;
	role: TWorkspaceMemberRole;
	image?: string;
	isOwner?: boolean;
}

export interface WorkspacePreferences {
	skipStageChangePrompt: boolean;
	linkedinFetchEnabled: boolean;
}

export interface Workspace {
	handle: string;
	isDefaultPage?: boolean;
	name: string;
	logo?: string;
	id: string;
	isPro?: boolean;
	pageUsers?: WorkspaceMember[];
	proEnabled?: boolean;
	currentUserRole: TWorkspaceMemberRole;
	savedFilter?: string;
	defaultStages?: string[];
	defaultQuickReplies?: IQuickReply[];
	isDiscoverable?: boolean;
	discoverableDomain?: string | null;
	referralsEnabled?: boolean | null;
	defaultFeedbackTemplates?: ISWorkspaceFeedbackTemplate[];
	/** From `settings/fetch-details` → `workspace.default_assignments`. */
	defaultAssignments?: ISAssignment[];
	defaultSubjectLine?: string | null;
	defaultInterviewName?: string | null;
	/** Only on `/user/me` pages[] (the app store); the API fills unset toggles with defaults. */
	preferences?: WorkspacePreferences;
	// LinkedIn integration
	linkedinCompanyId?: string | null;
	linkedinPosterEmail?: string | null;
	linkedinEnabled?: boolean | null;
	linkedinListingAcknowledged?: boolean | null;
	// Google Calendar Domain-Wide Delegation
	calendarDwd?: IGCalDWDInfo;
	/** Sent on both `/user/me` pages[] and `settings/fetch-details` → `workspace`. */
	mcpEnabled?: boolean;
}

export interface UpdateWorkspaceDefaultsPayload {
	workspace_id: string;
	default_subject_line: string | null;
	default_interview_name: string | null;
}

export interface UpdateWorkspaceDefaultsResponse {
	defaultSubjectLine: string | null;
	defaultInterviewName: string | null;
}

export interface UpdateWorkspacePreferencesPayload {
	workspace_id: string;
	/** Only the preferences sent are changed. */
	preferences: Partial<{
		skip_stage_change_prompt: boolean;
		linkedin_fetch_enabled: boolean;
	}>;
}

export interface UpdateWorkspacePreferencesResponse {
	preferences: WorkspacePreferences;
}

export interface UpdateWorkspaceDiscoverabilityPayload {
	page_id: string;
	is_discoverable: boolean;
}

export interface UpdateWorkspaceDiscoverabilityResponse {
	isDiscoverable: boolean;
}

export interface AddUserToWorkspaceRequest {
	workspace_id: string;
	user: {
		email: string;
		role: TWorkspaceMemberRole;
	};
}

export interface RemoveUserFromWorkspaceRequest {
	workspace_id: string;
	email: string;
}

export interface CreateWorkspacePayload {
	// Optional: during agentic onboarding the frontend creates the workspace with an
	// empty body and the backend derives name/company/ATS from the user record.
	name?: string;
}

export interface CreateWorkspaceResponse {
	id: string;
	type: TWorkspaceMemberRole;
	name: string;
	users: WorkspaceMember[];
}

export interface GetAllWorkspacesResponse {
	pages: Workspace[];
}

export interface AddUserToWorkspaceResponse {
	member: WorkspaceMember;
}

export interface UpdateWorkspaceImageResponse {
	imageUrl: string;
}

export interface UpdateWorkspaceNamePayload {
	name: string;
	page_id: string;
}

export interface ChangeUserRoleRequest {
	email: string;
	role: TWorkspaceMemberRole;
}
